/** @jsxImportSource @emotion/react */
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import menuBlackIcon from '../../assets/menu/menu-black.svg';
import ChevronRight from '../../assets/chevron-right.svg';

import menuStyles from './menu.styles';
import { useMediaQuery } from '@mui/material';
import PredefinedSwipableDrawer from '../swipable-edge-drawer/swipable-drawer.component';
import { NavLink } from 'react-router-dom';
import { PoweredBy } from '../../app/layouts/components/powered-by/powered-by.component';
import HistoryMenuList from '../history-menu-list/history-menu-list';

type TProps = {
    zIndex?: number;
};

export const Menu: FC<TProps> = props => {
    const { zIndex } = props;
    const intl = useIntl();
    const isDesktop = useMediaQuery('(min-width:600px)');
    const [modalIsOpen, setModalIsOpen] = useState(false);

    useEffect(() => {
        const overlayClickHandler = (e: MouseEvent) => {
            if (
                modalIsOpen &&
                e.target instanceof Element &&
                (!isDesktop
                    ? !e.target.closest('.MuiDrawer-root')
                    : !e.target.closest('#menuBlock')) &&
                !e.target.closest('#menuButton')
            ) {
                setModalIsOpen(false);
            }
        };

        document.addEventListener('click', overlayClickHandler);

        return () => {
            document.removeEventListener('click', overlayClickHandler);
        };
    }, [modalIsOpen, isDesktop]);

    const content = useMemo(() => {
        return (
            <>
                {!isDesktop && (
                    <p css={menuStyles.menuHeader}>{intl.formatMessage({ id: 'menu.title' })}</p>
                )}
                <div css={menuStyles.list}>
                    <NavLink
                        css={menuStyles.listElement}
                        onClick={() => setModalIsOpen(false)}
                        to={'/'}>
                        {intl.formatMessage({ id: 'menu.main' })}
                        <img
                            src={ChevronRight}
                            alt={'Chevron right'}
                            css={menuStyles.chevronRight}
                        />
                    </NavLink>
                    <HistoryMenuList
                        isMenuOpen={modalIsOpen}
                        onMenuClose={() => setModalIsOpen(false)}
                    />
                    <a
                        css={menuStyles.listElement}
                        onClick={() => setModalIsOpen(false)}
                        href={'https://grizzl-e.com/store'}
                        target={'_blank'}
                        rel="noreferrer">
                        {intl.formatMessage({ id: 'menu.shop' })}
                        <img
                            src={ChevronRight}
                            alt={'Chevron right'}
                            css={menuStyles.chevronRight}
                        />
                    </a>
                    <a
                        css={menuStyles.listElement}
                        onClick={() => setModalIsOpen(false)}
                        href={'https://grizzl-e.com/news'}
                        target={'_blank'}
                        rel="noreferrer">
                        {intl.formatMessage({ id: 'menu.news' })}
                        <img
                            src={ChevronRight}
                            alt={'Chevron right'}
                            css={menuStyles.chevronRight}
                        />
                    </a>
                    <a
                        css={menuStyles.listElement}
                        onClick={() => setModalIsOpen(false)}
                        href={'https://grizzl-e.com/contacts'}
                        target={'_blank'}
                        rel="noreferrer">
                        {intl.formatMessage({ id: 'menu.contacts' })}
                        <img
                            src={ChevronRight}
                            alt={'Chevron right'}
                            css={menuStyles.chevronRight}
                        />
                    </a>
                </div>
                <div css={menuStyles.menuFooter}>
                    <PoweredBy />
                </div>
            </>
        );
    }, [isDesktop, intl]);

    return (
        <>
            <div id="menuButton" css={[menuStyles.menuButton, menuStyles.menuButtonBlack]}>
                <img
                    onClick={() => setModalIsOpen(state => !state)}
                    src={menuBlackIcon}
                    alt={'Menu icon'}
                />
            </div>
            {isDesktop ? (
                <>
                    {modalIsOpen && (
                        <div id="menuBlock" css={[menuStyles.menuList, menuStyles.menuListDesktop]}>
                            {content}
                        </div>
                    )}
                </>
            ) : (
                <PredefinedSwipableDrawer
                    zIndex={zIndex}
                    onClose={() => setModalIsOpen(false)}
                    onOpen={() => setModalIsOpen(true)}
                    isOpen={modalIsOpen}
                    snapPoints={[0.6]}>
                    <div css={[menuStyles.menuList]}>{content}</div>
                </PredefinedSwipableDrawer>
            )}
        </>
    );
};
